'use client';

import { Box } from '@radix-ui/themes';
import React, { useEffect, useMemo, useRef, useCallback } from 'react';
import { Dropdown } from '../Dropdown';
import { useExplorer } from '@/hooks/useExplorer';
import { useFetchUser } from '@/auth/hooks';
import { STORAGE_KEY_SITE_ID } from '@/context/explorer';
import { useSearchParams } from 'next/navigation';
import { urlSiteNameKey } from '@/constant/Constants';
import { Loader } from 'lucide-react';
import { explorerAnalytics } from '@/lib/analytics';

interface ApiKeyEditBoxProps {
  handleApiKeyChange: (siteId: string, siteName: string) => void;
  isLoading?: boolean;
}

const SiteSelector: React.FC<ApiKeyEditBoxProps> = ({
  handleApiKeyChange,
  isLoading = false,
}) => {
  const { user } = useFetchUser();
  const { apiKey, siteName, validateAndClearStaleSites } = useExplorer();
  const searchParams = useSearchParams();
  const hasInitialized = useRef(false);

  const sites = useMemo(() => {
    if (!user?.authorized_sites || !user?.site_configurations) return [];

    return user.authorized_sites.filter((site, index) => {
      const config = user.site_configurations[index];
      return site.site_type === 'test' && config?.is_item_model === true;
    });
  }, [user]);

  useEffect(() => {
    if (sites) {
      validateAndClearStaleSites(sites);
    }
  }, [sites, validateAndClearStaleSites]);

  useEffect(() => {
    if (!siteName && hasInitialized.current) {
      hasInitialized.current = false;
    }
  }, [siteName]);

  const urlSiteName = searchParams.get(urlSiteNameKey);

  useEffect(() => {
    if (!sites || sites.length === 0) return;

    if (urlSiteName && !hasInitialized.current) {
      const urlSite = sites.find((site) => site.site_name === urlSiteName);
      if (urlSite) {
        hasInitialized.current = true;
        handleApiKeyChange(urlSite.site_id, urlSite.site_name);
        return;
      } else {
        if (typeof window !== 'undefined') {
          const url = new URL(window.location.href);
          url.searchParams.delete(urlSiteNameKey);
          window.history.replaceState({}, '', url.toString());
        }
      }
    }

    if (hasInitialized.current || (apiKey && siteName)) return;

    hasInitialized.current = true;

    const persistedSiteId =
      typeof window !== 'undefined'
        ? localStorage.getItem(STORAGE_KEY_SITE_ID)
        : null;

    if (persistedSiteId) {
      const persistedSite = sites.find(
        (site) => site.site_id === persistedSiteId
      );
      if (persistedSite) {
        handleApiKeyChange(persistedSite.site_id, persistedSite.site_name);
        return;
      }
    }

    handleApiKeyChange(sites[0].site_id, sites[0].site_name);
  }, [sites, apiKey, handleApiKeyChange, urlSiteName, siteName]);

  const handleDropdownChange = useCallback(
    (siteId: string, newSiteName: string) => {
      explorerAnalytics.siteChanged({
        fromSite: siteName || '',
        toSite: newSiteName,
        isLoggedIn: true,
      });
      hasInitialized.current = false;

      if (typeof window !== 'undefined' && urlSiteName) {
        const url = new URL(window.location.href);
        url.searchParams.delete(urlSiteNameKey);
        window.history.replaceState({}, '', url.toString());
      }
      handleApiKeyChange(siteId, newSiteName);
    },
    [handleApiKeyChange, urlSiteName, siteName]
  );

  return (
    <Box>
      {isLoading ? (
        <Loader className="size-6 animate-spin flex-shrink-0" />
      ) : (
        <Dropdown
          onChange={handleDropdownChange}
          options={{
            placeholder: 'Select a site',
            items:
              sites?.map((site) => ({
                key: site.site_id,
                value: site.site_name,
              })) ?? [],
          }}
          value={siteName}
        />
      )}
    </Box>
  );
};

export default SiteSelector;
